
/**
 * @deprecated This service is deprecated and has been replaced by semanticService.ts.
 * All module3 functionality has been integrated into module1 (port 8001).
 * Please use the semanticService for all semantic analysis operations going forward.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { snowflakeApi } from '../utils/api';
import { workflowService } from './api/workflowService';

// Interface for semantic analysis request
export interface SemanticAnalysisRequest {
  workflow_id: string;
  client_id: string;
  relevant_keywords_blob: string;
  undecided_keywords_blob?: string;
  topic_framework_blob: string;
  suggested_categories?: any[];
  forceReanalysis?: boolean;
}

// Interface for semantic analysis status
export interface SemanticAnalysisStatus {
  workflow_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
  message?: string;
  framework_id?: string;
}

// Interface for classification framework
export interface ClassificationFramework {
  id: string;
  client_id: string;
  name: string;
  description: string;
  categories: {
    id: string;
    name: string;
    definition: string;
    examples: string[];
    parent_id?: string;
  }[];
  created_at: string;
  updated_at: string;
  version: number;
}

// Interface for framework update
export interface FrameworkUpdateRequest {
  updates: {
    categories: {
      action: 'add' | 'update' | 'delete';
      data: Partial<ClassificationFramework['categories'][0]>;
    }[];
  };
  track_changes: boolean;
  user_id: string;
}

interface Category {
  id: string;
  name: string;
  description: string;
  patterns: string[];
}

interface Framework {
  categories: Category[];
  version: number;
  status: 'draft' | 'reviewed' | 'approved';
}

// Database-first interfaces
interface DbSemanticAnalysisRequest {
  workflowId: number;
  relevantKeywordsBlob: string;
  topicFrameworkBlob: string;
  forceReanalysis?: boolean;
  clientId?: number;
}

interface DbSemanticAnalysisResponse {
  moduleId: number;
  workflowId: number;
  status: string;
  message?: string;
  error?: string;
  frameworkId?: number;
  clientId?: number;
}

interface DbFrameworkRequest {
  moduleId: number;
  framework: {
    name: string;
    description: string;
    categories: {
      name: string;
      definition: string;
      examples: string[];
      parent_id?: number;
    }[];
  };
}

interface DbFrameworkUpdateRequest {
  frameworkId: number;
  updates: {
    categories: {
      action: 'add' | 'update' | 'delete';
      data: any;
    }[];
  };
  trackChanges: boolean;
  userId: number;
}

// Service implementation
export class Module3Service {
  private static instance: Module3Service;

  private constructor() {}

  static getInstance(): Module3Service {
    if (!Module3Service.instance) {
      Module3Service.instance = new Module3Service();
    }
    return Module3Service.instance;
  }

  // Helper method to get Application Insights
  private getAppInsights() {
    return (window as any).__appInsights || null;
  }

  async getHealth() {
    // Module3 has been integrated into module1, so we return a static success response
    // instead of making a separate health check request to a non-existent endpoint
    return { 
      status: 'healthy',
      module: 'module3',
      name: 'Semantic Classification (integrated in module1)',
      timestamp: new Date().toISOString(),
      integrated: true
    };
  }

  public async getFramework(workflowId: string): Promise<Framework> {
    console.log('Getting framework:', workflowId);
    try {
      const response = await snowflakeApi.get(`/api/v1/framework/${workflowId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting framework:', error);
      // Fallback to mock response
      return {
        categories: [
          {
            id: '1',
            name: 'Category 1',
            description: 'Example category',
            patterns: ['pattern1', 'pattern2']
          }
        ],
        version: 1,
        status: 'draft'
      };
    }
  }

  public async saveFramework(workflowId: string, framework: Framework): Promise<void> {
    console.log('Saving framework:', workflowId, framework);
    try {
      await snowflakeApi.post(`/api/v1/framework/${workflowId}`, framework);
    } catch (error) {
      console.error('Error saving framework:', error);
    }
  }

  // Create semantic analysis
  public async startAnalysis(request: SemanticAnalysisRequest): Promise<SemanticAnalysisStatus> {
    console.log('Starting analysis:', request);
    
    // Track API call start in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `module3-analysis-${request.workflow_id}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'Module3API_AnalysisStarted',
        properties: {
          correlationId,
          workflowId: request.workflow_id,
          clientId: request.client_id,
          operation: 'startAnalysis',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    // Prepare request with default values for required fields if not provided
    const apiRequest = {
      ...request,
      // Default value for undecided_keywords_blob if not provided
      undecided_keywords_blob: request.undecided_keywords_blob || request.relevant_keywords_blob.replace('relevant.csv', 'undecided.csv'),
      // Default empty array for suggested_categories if not provided
      suggested_categories: request.suggested_categories || []
    };
    
    // Use the correct endpoint - /api/v1/analyze
    const response = await snowflakeApi.post('/api/v1/analyze', apiRequest);
    
    // Track successful API call in Application Insights
    if (appInsights) {
      const duration = Date.now() - startTime;
      appInsights.trackEvent({
        name: 'Module3API_AnalysisCompleted',
        properties: {
          correlationId,
          workflowId: request.workflow_id,
          clientId: request.client_id,
          operation: 'startAnalysis',
          responseStatus: response.data?.status || 'unknown',
          durationMs: duration,
          timestamp: new Date().toISOString()
        }
      });
    }
    
    return response.data;
  }

  // Get semantic analysis status
  public async getAnalysisStatus(workflowId: string): Promise<SemanticAnalysisStatus> {
    console.log('Getting analysis status:', workflowId);
    // Use the correct endpoint pattern - /api/v1/workflows/{workflowId}/status
    const response = await snowflakeApi.get(`/api/v1/workflows/${workflowId}/status`);
    return response.data;
  }

  // Get framework by ID
  public async getFrameworkById(frameworkId: string): Promise<ClassificationFramework> {
    console.log('Getting framework by ID:', frameworkId);
    try {
      const response = await snowflakeApi.get(`/api/v1/framework/id/${frameworkId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting framework by ID:', error);
      // Fallback to mock response
      return {
        id: frameworkId,
        client_id: 'mock-client',
        name: 'Mock Framework',
        description: 'Mock framework for development',
        categories: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        version: 1
      };
    }
  }

  // Update framework
  public async updateFramework(
    frameworkId: string, 
    updateRequest: FrameworkUpdateRequest
  ): Promise<ClassificationFramework> {
    console.log('Updating framework:', frameworkId, updateRequest);
    try {
      const response = await snowflakeApi.put(`/api/v1/framework/${frameworkId}`, updateRequest);
      return response.data;
    } catch (error) {
      console.error('Error updating framework:', error);
      // Fallback to mock response
      return {
        id: frameworkId,
        client_id: 'mock-client',
        name: 'Mock Framework',
        description: 'Mock framework for development',
        categories: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        version: 2
      };
    }
  }

  // DATABASE-FIRST METHODS
  
  // Start semantic analysis using database workflow ID
  public async startAnalysisDb(params: DbSemanticAnalysisRequest): Promise<DbSemanticAnalysisResponse> {
    console.log('Starting semantic analysis with database workflow ID:', params.workflowId);
    
    // Track API call start in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `db-semantic-analysis-${params.workflowId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_SemanticAnalysisStarted',
        properties: {
          correlationId,
          workflowId: params.workflowId,
          clientId: params.clientId,
          operation: 'startAnalysisDb',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      // Use the workflowService method for starting semantic analysis
      const data = await workflowService.startDbSemanticAnalysis(
        params.workflowId, 
        params.relevantKeywordsBlob, 
        params.topicFrameworkBlob, 
        params.forceReanalysis,
        params.clientId
      );
      
      console.log('DB-first semantic analysis response:', data);
      
      // Track successful API call in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_SemanticAnalysisCompleted',
          properties: {
            correlationId,
            workflowId: params.workflowId,
            clientId: params.clientId,
            moduleId: data.module_id,
            operation: 'startAnalysisDb',
            responseStatus: data?.status || 'unknown',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return data;
    } catch (error) {
      console.error('DB-first semantic analysis error:', error);
      
      // Track API error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in DB-first semantic analysis'),
          properties: {
            correlationId,
            workflowId: params.workflowId,
            clientId: params.clientId,
            operation: 'startAnalysisDb',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }

  // Get semantic analysis status by workflow ID
  public async getAnalysisStatusByWorkflowDb(workflowId: number, clientId?: number): Promise<any> {
    console.log(`Getting semantic analysis status for workflow ID ${workflowId}`);
    
    // Track API call in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `db-semantic-status-workflow-${workflowId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_SemanticStatusByWorkflowStarted',
        properties: {
          correlationId,
          workflowId,
          clientId,
          operation: 'getAnalysisStatusByWorkflowDb',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      // Use the workflowService method to get analysis status by workflow ID
      const data = await workflowService.getDbSemanticAnalysisByWorkflow(workflowId, clientId);
      
      // Track status check result in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_SemanticStatusByWorkflowCompleted',
          properties: {
            correlationId,
            workflowId,
            clientId,
            moduleId: data?.module_id,
            operation: 'getAnalysisStatusByWorkflowDb',
            responseStatus: data?.status || 'unknown',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return data;
    } catch (error) {
      console.error(`Error getting DB semantic analysis status for workflow ${workflowId}:`, error);
      
      // Track error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in DB semantic status by workflow check'),
          properties: {
            correlationId,
            workflowId,
            clientId,
            operation: 'getAnalysisStatusByWorkflowDb',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }

  // Get semantic analysis status by module ID
  public async getAnalysisStatusDb(moduleId: number): Promise<any> {
    console.log(`Getting semantic analysis status for module ID ${moduleId}`);
    
    // Track API call in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `db-semantic-status-${moduleId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_SemanticStatusCheckStarted',
        properties: {
          correlationId,
          moduleId,
          operation: 'getAnalysisStatusDb',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      const response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}/semantic`);
      
      // Track status check result in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_SemanticStatusCheckCompleted',
          properties: {
            correlationId,
            moduleId,
            operation: 'getAnalysisStatusDb',
            responseStatus: response.data?.status || 'unknown',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error getting DB semantic analysis status:', error);
      
      // Track error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in DB semantic status check'),
          properties: {
            correlationId,
            moduleId,
            operation: 'getAnalysisStatusDb',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }

  // Get framework by workflow ID
  public async getFrameworkByWorkflowId(workflowId: number, clientId?: number): Promise<any> {
    console.log(`Getting framework for workflow ID ${workflowId}`);
    
    // Track API call in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `db-framework-workflow-${workflowId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_FrameworkByWorkflowStarted',
        properties: {
          correlationId,
          workflowId,
          clientId,
          operation: 'getFrameworkByWorkflowId',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      // Use the workflowService method to get framework by workflow ID
      const data = await workflowService.getDbFrameworkByWorkflow(workflowId, clientId);
      
      // Track result in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_FrameworkByWorkflowCompleted',
          properties: {
            correlationId,
            workflowId,
            clientId,
            moduleId: data?.module_id,
            frameworkId: data?.framework_id,
            operation: 'getFrameworkByWorkflowId',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return data;
    } catch (error) {
      console.error(`Error getting framework for workflow ${workflowId}:`, error);
      
      // Track error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in DB framework by workflow'),
          properties: {
            correlationId,
            workflowId,
            clientId,
            operation: 'getFrameworkByWorkflowId',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }

  // Get framework by module ID
  public async getFrameworkByModuleId(moduleId: number): Promise<any> {
    console.log(`Getting framework for module ID ${moduleId}`);
    
    try {
      const response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}/framework`);
      return response.data;
    } catch (error) {
      console.error('Error getting DB framework by module:', error);
      throw error;
    }
  }

  // Get framework by framework ID (direct database ID)
  public async getFrameworkByDbId(frameworkId: number): Promise<any> {
    console.log(`Getting framework by database ID ${frameworkId}`);
    
    try {
      const response = await snowflakeApi.get(`/api/v1/db/framework/${frameworkId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting framework by DB ID:', error);
      throw error;
    }
  }

  // Create or update framework
  public async saveFrameworkDb(request: DbFrameworkRequest): Promise<any> {
    console.log('Saving framework for module ID:', request.moduleId);
    
    try {
      const response = await snowflakeApi.post(`/api/v1/db/module/${request.moduleId}/framework`, request.framework);
      return response.data;
    } catch (error) {
      console.error('Error saving DB framework:', error);
      throw error;
    }
  }

  // Update framework with changes
  public async updateFrameworkDb(request: DbFrameworkUpdateRequest): Promise<any> {
    console.log('Updating framework with database ID:', request.frameworkId);
    
    try {
      const response = await snowflakeApi.put(`/api/v1/db/framework/${request.frameworkId}`, {
        updates: request.updates,
        track_changes: request.trackChanges,
        user_id: request.userId
      });
      return response.data;
    } catch (error) {
      console.error('Error updating DB framework:', error);
      throw error;
    }
  }

  // Export framework to Module 4 configuration
  public async exportFrameworkToModule4(
    frameworkId: string,
    config: {
      config_id?: string;
      name?: string;
      description?: string;
    } = {}
  ): Promise<any> {
    console.log(`Exporting framework ${frameworkId} to Module 4 configuration`);
    
    // Track API call in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `export-module4-${frameworkId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'Module3API_ExportToModule4Started',
        properties: {
          correlationId,
          frameworkId,
          operation: 'exportFrameworkToModule4',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      const response = await snowflakeApi.post(`/api/v1/framework/${frameworkId}/export/module4-config`, config);
      
      // Track result in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'Module3API_ExportToModule4Completed',
          properties: {
            correlationId,
            frameworkId,
            operation: 'exportFrameworkToModule4',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error exporting framework ${frameworkId} to Module 4 configuration:`, error);
      
      // Track error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error exporting to Module 4'),
          properties: {
            correlationId,
            frameworkId,
            operation: 'exportFrameworkToModule4',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }

  // Get all available frameworks
  public async getAllFrameworks(clientId?: string): Promise<ClassificationFramework[]> {
    console.log('Getting all frameworks');
    try {
      const response = await snowflakeApi.get('/api/v1/framework', {
        params: clientId ? { client_id: clientId } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error getting frameworks:', error);
      // Fallback to mock response
      return [
        {
          id: 'mock-framework-1',
          client_id: clientId || 'mock-client',
          name: 'Mock Framework 1',
          description: 'First mock framework for development',
          categories: [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          version: 1
        },
        {
          id: 'mock-framework-2',
          client_id: clientId || 'mock-client',
          name: 'Mock Framework 2',
          description: 'Second mock framework for development',
          categories: [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          version: 1
        }
      ];
    }
  }
}

// React Query hooks for database-first operations
export const useStartSemanticAnalysisDb = () => {
  return useMutation({
    mutationFn: (params: DbSemanticAnalysisRequest) => 
      Module3Service.getInstance().startAnalysisDb(params),
  });
};

// Get semantic analysis status by workflow ID
export const useGetSemanticAnalysisStatusByWorkflowDb = (workflowId: number, clientId?: number) => {
  return useQuery({
    queryKey: ['dbSemanticAnalysisStatus', 'workflow', workflowId, clientId],
    queryFn: () => Module3Service.getInstance().getAnalysisStatusByWorkflowDb(workflowId, clientId),
    enabled: !!workflowId,
    refetchInterval: 10000 // Poll every 10 seconds
  });
};

// Get semantic analysis status by module ID 
export const useGetSemanticAnalysisStatusDb = (moduleId: number) => {
  return useQuery({
    queryKey: ['dbSemanticAnalysisStatus', 'module', moduleId],
    queryFn: () => Module3Service.getInstance().getAnalysisStatusDb(moduleId),
    enabled: !!moduleId,
    refetchInterval: 10000 // Poll every 10 seconds
  });
};

// Get framework by workflow ID
export const useGetFrameworkByWorkflowId = (workflowId: number, clientId?: number) => {
  return useQuery({
    queryKey: ['dbFramework', 'workflow', workflowId, clientId],
    queryFn: () => Module3Service.getInstance().getFrameworkByWorkflowId(workflowId, clientId),
    enabled: !!workflowId
  });
};

// Get framework by module ID
export const useGetFrameworkByModuleId = (moduleId: number) => {
  return useQuery({
    queryKey: ['dbFramework', 'module', moduleId],
    queryFn: () => Module3Service.getInstance().getFrameworkByModuleId(moduleId),
    enabled: !!moduleId
  });
};

export const useSaveFrameworkDb = () => {
  return useMutation({
    mutationFn: (request: DbFrameworkRequest) => 
      Module3Service.getInstance().saveFrameworkDb(request),
  });
};

export const useUpdateFrameworkDb = () => {
  return useMutation({
    mutationFn: (request: DbFrameworkUpdateRequest) => 
      Module3Service.getInstance().updateFrameworkDb(request),
  });
};

export const useExportFrameworkToModule4 = () => {
  return useMutation({
    mutationFn: ({ frameworkId, config }: { frameworkId: string, config?: any }) => 
      Module3Service.getInstance().exportFrameworkToModule4(frameworkId, config),
  });
};

export const useGetAllFrameworks = (clientId?: string) => {
  return useQuery({
    queryKey: ['frameworks', clientId],
    queryFn: () => Module3Service.getInstance().getAllFrameworks(clientId),
  });
};

export default Module3Service;
